import { monthBounds, moodColor, moodLabel, shiftDay, shiftMonth, signedFigure, type MoodCheckin, type ThoughtRecord, type VoiceLog } from '@adhd/shared';
import { useCallback, useEffect, useState } from 'react';
import { useNow, useOnResume } from '../lib/fresh.js';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { DayDoses } from '../components/DayDoses.js';
import { DayFeed, type FeedItem } from '../components/DayFeed.js';
import { MarkChips } from '../components/MarkChips.js';
import { DayPeopleRow } from '../components/DayPeopleRow.js';
import { DaySummary } from '../components/DaySummary.js';
import { ChartIcon, ClipboardIcon, PrinterIcon, ShareIcon } from '../components/Icons.js';
import { api, ApiError, type MonthPage, type OwnLifeEvent } from '../lib/api.js';
import { covering } from '../components/DayLifeEvent.js';
import { ThreadChips } from '../components/ThreadChips.js';
import { PastDayEntry } from '../components/PastDayEntry.js';
import { WeeklyRecaps } from '../components/WeeklyRecaps.js';
import { bandOf, inkOn } from '../lib/band.js';
import { monthName } from './Month.js';

/**
 * Days: the calendar before the list. Release 6 of 2.0 (docs/PRD-2.0.md §6).
 *
 * The Log answered "what did I write?" and never "what was last Tuesday
 * like?" or "how long was the gap?" — the two questions ADHD time-blindness
 * actually asks. The month as a grid, each day coloured by where it ended in
 * the seven condition colours, a dot where he wrote, blank where nothing; and
 * the day he taps opens underneath as the same line of time Today draws, with
 * its line, its marks and its people. The list stays at /history for reading
 * straight through; everything the Log did is one tap from here.
 *
 * The cells come from the same figures the month page prints
 * (`GET /insights/months/:m?grid=1`), so a day is the same colour on every
 * screen that draws it and paging through months commissions nothing.
 */
export function Days(): JSX.Element {
  const { month: param } = useParams<{ month?: string }>();
  const [search, setSearch] = useSearchParams();
  // The moving clock (lib/fresh.tsx): a Days left open overnight moves its
  // "today" when the date does (20 Oct 2026).
  const todayKey = localDay(useNow());
  const pickedParam = search.get('day');
  const month = param && /^\d{4}-\d{2}$/.test(param) ? param : pickedParam ? pickedParam.slice(0, 7) : todayKey.slice(0, 7);
  const picked = pickedParam && pickedParam.slice(0, 7) === month ? pickedParam : month === todayKey.slice(0, 7) ? todayKey : null;

  const [page, setPage] = useState<MonthPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<string[] | null>(null);
  const [unfinished, setUnfinished] = useState(0);

  useEffect(() => {
    setPage(null);
    setError(null);
    void api
      .month(month, true)
      .then(setPage)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Couldn't load that month."));
  }, [month]);
  const loadAround = (): void => {
    void api.activeDays().then(setActive).catch(() => setActive([]));
    void api
      .listThoughtRecords('?limit=100')
      .then((r) => setUnfinished(r.items.filter((t) => !t.isComplete).length))
      .catch(() => {});
  };
  useEffect(loadAround, []);
  /* Back in the app after a minute away: the month's colours and the lists
     again, in place — the grid does not blank while it refetches (20 Oct 2026). */
  useOnResume(() => {
    void api.month(month, true).then(setPage).catch(() => {});
    loadAround();
  });

  const first = active && active.length > 0 ? active[active.length - 1]! : null;
  const prev = shiftMonth(month, -1);
  const next = shiftMonth(month, 1);
  const nextAllowed = next <= todayKey.slice(0, 7);

  return (
    <div className="stack-lg stack days">
      <header className="page-header">
        <div className="row-between" style={{ alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <h1>Days</h1>
          <span className="row" style={{ gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <Link className="linkish" to="/export">
              <PrinterIcon /> Print
            </Link>
            <Link className="linkish" to="/circle">
              <ShareIcon /> Circle
            </Link>
            <Link className="linkish" to="/homework">
              <ClipboardIcon /> Homework
            </Link>
            <Link className="linkish" to="/trends">
              <ChartIcon /> Insights
            </Link>
          </span>
        </div>
        <p>
          {active === null
            ? ' '
            : first
              ? `Since ${formatShort(first)} · ${active.length} ${active.length === 1 ? 'day' : 'days'} logged`
              : 'Nothing logged yet. The first day you log is the first cell.'}
        </p>
      </header>

      <div className="days-split">
        <section className="stack-sm stack">
          <div className="cal-nav">
            <Link className="linkish" to={`/days/${prev}`} aria-label={`${monthName(prev)}`}>
              ‹ {monthName(prev).replace(/ \d{4}$/, '')}
            </Link>
            <h2>{monthName(month)}</h2>
            {nextAllowed ? (
              <Link className="linkish" to={`/days/${next}`} aria-label={`${monthName(next)}`}>
                {monthName(next).replace(/ \d{4}$/, '')} ›
              </Link>
            ) : (
              <span />
            )}
          </div>
          {error ? (
            <div className="banner banner-error">{error}</div>
          ) : page === null ? (
            <div style={{ display: 'grid', placeItems: 'center', minHeight: '14rem' }}>
              <span className="spinner" />
            </div>
          ) : (
            <MonthGrid page={page} picked={picked} today={todayKey} onPick={(d) => setSearch(d === picked ? {} : { day: d })} />
          )}
          <p className="faint">Colour is where the day ended. A dot: you wrote. Blank: no check-in. Tap a day.</p>
          {unfinished > 0 && (
            <Link className="option option-row" to="/history">
              <strong className="grow">Worth finishing</strong>
              <span>
                {unfinished} ›
              </span>
            </Link>
          )}
          {/* The weeks and the month, here since 14 Oct 2026: the log kept
              them, and the log is now only the list. */}
          <WeeklyRecaps compact />
          <div className="row" style={{ gap: 'var(--space-4)', flexWrap: 'wrap' }}>
            <Link className="linkish linkish-quiet" to="/month">This month, as one page ›</Link>
            <Link className="linkish linkish-quiet" to="/history">The list, newest first ›</Link>
          </div>
        </section>

        <section className="stack">{picked ? <DayPanel key={picked} day={picked} today={todayKey} /> : <p className="muted">Tap a day to open it.</p>}</section>
      </div>
    </div>
  );
}

/** Monday-first, in the owner's calendar. Days after today are not drawn. */
function MonthGrid({ page, picked, today, onPick }: { page: MonthPage; picked: string | null; today: string; onPick: (day: string) => void }): JSX.Element {
  const { from } = monthBounds(page.figures.month);
  const byDay = new Map(page.figures.strip.map((d) => [d.day, d]));
  const firstDow = (new Date(`${from}T12:00:00Z`).getUTCDay() + 6) % 7; // Monday = 0
  const cells: (string | null)[] = [...Array(firstDow).fill(null)];
  const { to } = monthBounds(page.figures.month);
  for (let d = from; d <= to; d = shiftDay(d, 1)) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return (
    <div className="cal" role="grid" aria-label={`${monthName(page.figures.month)}: ${page.figures.rated} of ${page.figures.days} days rated`}>
      {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((w, i) => (
        <span key={i} className="cal-dh" aria-hidden="true">
          {w}
        </span>
      ))}
      {cells.map((d, i) => {
        if (d === null) return <span key={`b${i}`} className="cal-blank" />;
        const cell = byDay.get(d);
        if (d > today || !cell) return <span key={d} className="cal-future" aria-hidden="true">{Number(d.slice(8))}</span>;
        const band = cell.end === null ? null : bandOf(cell.end);
        const cls = ['cal-d', band ? `cal-${band} cal-ink-${inkOn(band)}` : cell.empty ? 'cal-empty' : 'cal-quiet', d === today ? 'cal-today' : '', d === picked ? 'cal-picked' : ''].filter(Boolean).join(' ');
        return (
          <button key={d} type="button" className={cls} aria-pressed={d === picked} aria-label={`${formatLong(d)}${cell.end === null ? (cell.empty ? ', nothing logged' : ', no check-in') : ''}${cell.wrote ? ', wrote' : ''}`} onClick={() => onPick(d)}>
            <span>{Number(d.slice(8))}</span>
            {cell.wrote && <i aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );
}

/**
 * One day, opened: the same line of time Today draws, with its line, its
 * marks and its people. Fetched by the day's own bounds rather than from a
 * capped list, so a day in August is as reachable as yesterday.
 */
function DayPanel({ day, today }: { day: string; today: string }): JSX.Element {
  // The threads running on this day, as chips — the one rendering of a thread
  // on a day (14 Oct 2026); their controls are on their own pages.
  const [events, setEvents] = useState<OwnLifeEvent[]>([]);
  const loadEvents = (): void => {
    void api.lifeEvents().then(setEvents).catch(() => {});
  };
  useEffect(loadEvents, [day]);
  const [items, setItems] = useState<FeedItem[] | null>(null);
  const [records, setRecords] = useState<Map<string, ThoughtRecord>>(new Map());
  const [sos, setSos] = useState(0);
  const [opened, setOpened] = useState(false);

  const load = useCallback(() => {
    // By the frozen day column, never an instant range: a 1am entry filed
    // under the night before has a recorded_at on the next date (14 Oct 2026).
    void Promise.all([
      api.listThoughtRecords(`?limit=100&day=${day}`),
      api.listCheckinsOn(day),
      api.listJournalOn(day),
      api.sosDays(),
    ])
      .then(([rs, cs, js, sosDays]) => {
        setSos(sosDays.find((s) => s.day === day)?.n ?? 0);
        const recs = rs.items.filter((r) => r.day === day);
        const recordsById = new Map(recs.map((r) => [r.id, r] as const));
        const derived = new Map<string, ThoughtRecord>();
        for (const j of js.items) {
          if (j.linkedThoughtRecordId && recordsById.has(j.linkedThoughtRecordId)) derived.set(j.id, recordsById.get(j.linkedThoughtRecordId)!);
        }
        const nested = new Set([...derived.values()].map((r) => r.id));
        const feed: FeedItem[] = [
          ...recs.filter((r) => !nested.has(r.id)).map((r) => ({ kind: 'record' as const, at: r.occurredAt, data: r })),
          ...cs.items.filter((c: MoodCheckin) => c.day === day).map((c) => ({ kind: 'checkin' as const, at: c.recordedAt, data: c })),
          ...js.items.filter((j: VoiceLog) => j.day === day).map((j) => ({ kind: 'journal' as const, at: j.recordedAt, data: j })),
        ].sort((a, b) => b.at.localeCompare(a.at));
        setRecords(derived);
        setItems(feed);
      })
      .catch(() => setItems([]));
  }, [day]);
  useEffect(load, [load]);
  // The opened day, again, on coming back (20 Oct 2026).
  useOnResume(() => {
    load();
    loadEvents();
  });

  const moods = (items ?? []).filter((i): i is Extract<FeedItem, { kind: 'checkin' }> => i.kind === 'checkin').map((c) => c.data.mood);
  const n = (k: FeedItem['kind']): number => (items ?? []).filter((i) => i.kind === k).length;
  const tally = [
    n('checkin') > 0 && `${n('checkin')} check-in${n('checkin') === 1 ? '' : 's'}`,
    sos > 0 && `${sos} Help now`,
    n('journal') > 0 && `${n('journal')} entr${n('journal') === 1 ? 'y' : 'ies'}`,
    n('record') > 0 && `${n('record')} thought record${n('record') === 1 ? '' : 's'}`,
  ].filter(Boolean) as string[];

  return (
    <article className="stack day-section day-section-ruled" id={`day-${day}`}>
      <div className="day-head">
        <div className="row-between" style={{ alignItems: 'baseline' }}>
          <h2>{day === today ? 'Today' : formatLong(day)}</h2>
          {/* Where the day ended: the last check-in's word and figure, as the cell is coloured. */}
          {moods.length > 0 && (
            <span className="day-end" style={{ color: moodColor(moods[0]!) }}>
              {moodLabel(moods[0]!)} {signedFigure(moods[0]!)}
            </span>
          )}
        </div>
        {items !== null && <p className="faint">{tally.length > 0 ? tally.join(' · ') : 'Nothing logged.'}</p>}
      </div>
      <ThreadChips events={covering(events, day)} day={day} />
        <MarkChips day={day} />
      <DayPeopleRow day={day} voiceLogId={null} />
      <details className="day-summary-fold" onToggle={(e) => { if ((e.currentTarget as HTMLDetailsElement).open) setOpened(true); }}>
        <summary>How this day went</summary>
        <DaySummary day={day} active={opened} shownNearby={{ checkins: true, entries: true }} />
      </details>
      <DayDoses day={day} active />
      {items === null ? (
        <div style={{ display: 'grid', placeItems: 'center', minHeight: '6rem' }}>
          <span className="spinner" />
        </div>
      ) : (
        <>
          <DayFeed items={items} records={records} today={today} onCheckinRemoved={load} />
          {/* A finished day may still be written about (14 Oct 2026, his "let's try it"). */}
          {day < today && <PastDayEntry day={day} onSaved={load} />}
        </>
      )}
      {day === today && (
        <Link className="linkish" to="/">
          Today, with the slider ›
        </Link>
      )}
    </article>
  );
}

function localDay(date: Date): string {
  return date.toLocaleDateString('en-CA');
}
function formatShort(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}
function formatLong(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short', timeZone: 'UTC' });
}
